export interface GovernanceContinuityIncidentLike {
  investigationStatus?: string | null;
  reportable?: boolean | null;
  commissionNotified?: boolean | null;
  portalReporterUserId?: string | null;
}

export interface GovernanceContinuityComplaintLike {
  status?: string | null;
  escalationLevel?: number | null;
}

export interface GovernanceContinuityFeedbackLike {
  feedbackType?: string | null;
  status?: string | null;
  source?: string | null;
}

export interface GovernanceWorkflowContinuitySummary {
  activeIncidents: number;
  pendingSirsNotifications: number;
  portalReportedIncidents: number;
  openComplaints: number;
  escalatedComplaints: number;
  openFeedback: number;
  complaintFeedbackItems: number;
  portalFeedbackItems: number;
  governanceFollowUpItems: number;
  blockedGovernanceItems: number;
}

const ACTIVE_INCIDENT_STATUSES = new Set(['REPORTED', 'UNDER_INVESTIGATION', 'ACTION_REQUIRED']);
const CLOSED_COMPLAINT_STATUSES = new Set(['RESOLVED', 'CLOSED', 'WITHDRAWN']);
const ACTIVE_FEEDBACK_STATUSES = new Set(['OPEN', 'IN_PROGRESS']);

export function summariseGovernanceWorkflowContinuity(input: {
  incidents: GovernanceContinuityIncidentLike[];
  complaints: GovernanceContinuityComplaintLike[];
  feedback: GovernanceContinuityFeedbackLike[];
}): GovernanceWorkflowContinuitySummary {
  const incidents = input.incidents || [];
  const complaints = input.complaints || [];
  const feedback = input.feedback || [];

  const activeIncidents = incidents.filter((incident) =>
    ACTIVE_INCIDENT_STATUSES.has((incident.investigationStatus || '').toUpperCase()),
  ).length;

  const pendingSirsNotifications = incidents.filter(
    (incident) => incident.reportable && !incident.commissionNotified,
  ).length;

  const portalReportedIncidents = incidents.filter((incident) =>
    Boolean(incident.portalReporterUserId),
  ).length;

  const openComplaints = complaints.filter(
    (complaint) => !CLOSED_COMPLAINT_STATUSES.has((complaint.status || '').toUpperCase()),
  ).length;

  const escalatedComplaints = complaints.filter(
    (complaint) =>
      (complaint.status || '').toUpperCase() === 'ESCALATED' ||
      Number(complaint.escalationLevel || 0) > 0,
  ).length;

  const openFeedback = feedback.filter((item) =>
    ACTIVE_FEEDBACK_STATUSES.has((item.status || '').toUpperCase()),
  ).length;

  const complaintFeedbackItems = feedback.filter(
    (item) => (item.feedbackType || '').toLowerCase() === 'complaint',
  ).length;

  const portalFeedbackItems = feedback.filter((item) =>
    ['consumer', 'family'].includes((item.source || '').toLowerCase()),
  ).length;

  return {
    activeIncidents,
    pendingSirsNotifications,
    portalReportedIncidents,
    openComplaints,
    escalatedComplaints,
    openFeedback,
    complaintFeedbackItems,
    portalFeedbackItems,
    governanceFollowUpItems: activeIncidents + openComplaints + openFeedback,
    blockedGovernanceItems: pendingSirsNotifications + escalatedComplaints,
  };
}
